import {
    useEffect,
    useState,
} from 'react';
import {DefectSignaturesFilter} from '../types';

// Module-scoped filter store (global-filter-store-pattern). The Filter button
// (which shows the active-count badge), the filter drawer and the list hook are
// separate components and must observe the same filter.
//
// Only the three DefectSignaturesFilterInput fields are supported server-side:
//   actionCode, status, verificationResult
//
// NO DEFAULT FILTER is seeded. It is tempting to default to status=NEW so the
// page opens on "untriaged" — but a default filter that the operator did not
// set makes the table silently partial, and an ACKNOWLEDGED defect that starts
// recurring would be invisible on the page whose whole job is showing what is
// broken. The summary strip surfaces the live count instead.
let globalFilters: DefectSignaturesFilter = {};
const filterListeners: Set<(filters: DefectSignaturesFilter) => void> = new Set();

const notifyFilterChange = (filters: DefectSignaturesFilter) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useDefectSignaturesFilter = () => {
    const [filters, setFilters] = useState<DefectSignaturesFilter>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: DefectSignaturesFilter) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        setFilters(globalFilters);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: DefectSignaturesFilter) => {
        // Drop empty values at the edge so the active-count badge counts only
        // filters that actually narrow anything.
        const clean = Object.entries(newFilters)
                            .reduce((acc: Record<string, string>, [key, value]) => {
                                if (typeof value === 'string' && value.trim() !== '') {
                                    acc[key] = value.trim();
                                }
                                return acc;
                            }, {});
        notifyFilterChange(clean);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        activeCount: Object.keys(filters).length,
        handleSetFilters,
        handleClearFilters,
    };
};
